import { yupResolver } from "@hookform/resolvers/yup"
import {
  Accordion,
  Button,
  FileInput,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Tabs,
  Textarea,
} from "@mantine/core"
import { useDisclosure, useId } from "@mantine/hooks"
import {
  IconArchive,
  IconArrowBackUp,
  IconDeviceFloppy,
  IconStatusChange,
  IconTrash,
  IconUpload,
  IconUser,
} from "@tabler/icons-react"
import { debounce, filter } from "lodash"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import * as yup from "yup"
import { CollectionQuery } from "../../../models/collection.model"
import { Property } from "../../../models/property.model"
import {
  TenantRequest,
  TenantRequestStatus,
} from "../../../models/tenant-request"
import { Tenant } from "../../../models/tenant.model"
import { Unit } from "../../../models/unit.model"
import ActivityLogWrapperComponent from "../../../shared/component/ActivityLog/activityLog-wrapper-component"
import Confirmation from "../../../shared/component/confirmation/action-confirmation"
import { Constants } from "../../../shared/constants/Constants"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { useLazyGetActivitiesQuery } from "../../../shared/store/query/sharedQuery"
import {
  useLazyGetPropertiesOfLeaseQuery,
  useLazyGetTenantsOfLeaseQuery,
  useLazyGetUnitsOfLeaseQuery,
} from "../../lease/store/lease.query"
import {
  useArchiveRequestMutation,
  useAssignRequestMutation,
  useChangeRequestStatusMutation,
  useCreateRequestMutation,
  useDeleteRequestMutation,
  useLazyGetArchivedRequestQuery,
  useLazyGetRequestQuery,
  useRemoveRequestFileMutation,
  useRestoreRequestMutation,
  useToggleRequestStatusMutation,
  useUpdateRequestMutation,
} from "../store/request.query"
import { useLazyGetOwnersQuery } from "../../owner/store/owner.query"
import { Owner } from "../../../models/owner.model"
import { notification } from "../../../shared/component/notification/utility/notification"
import FileViewerModal from "../../../shared/component/file-viewer/file-viewer-modal.component"
import LoadingInputWrapper from "../../../shared/component/LoadingInputWrapper"
interface Props {
  editMode: string
}

export default function NewRequestComponent(props: Props) {
  const { editMode } = props
  const id = useId()
  const params = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const [opened, { open, close }] = useDisclosure()
  const [statusOpened, { open: openStatus, close: closeStatus }] =
    useDisclosure()
  const [fileOpened, { open: fileOpen, close: fileClose }] = useDisclosure()
  const [selectedFiles, setSelectedFiles] = useState<string[]>()
  const [userInfo, currentUserId, ownerInfo, tenantInfo] = useCurrentUser()
  const [propertySearch, setPropertySearch] = useState<string | null>(null)
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null)
  const [unitSearch, setUnitSearch] = useState<string | null>(null)
  const [tenantSearch, setTenantSearch] = useState<string | null>(null)
  const [requestStatus, setRequestStatus] = useState<TenantRequestStatus>(
    TenantRequestStatus.Pending,
  )
  const [propertiesCollection, setPropertiesCollection] =
    useState<CollectionQuery>({
      top: 10,
      skip: 0,
    })
  const [unitCollection, setUnitCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })
  const [tenantsCollection, setTenantsCollection] = useState<CollectionQuery>({
    top: 10,
    skip: 0,
  })

  let schema: any = yup.object().shape({
    propertyId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("property", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(
        t("requiredValidation", { field: t("property", { count: 1 }) }),
      ),
    unitId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("unit", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(t("requiredValidation", { field: t("unit", { count: 1 }) })),
    tenantId: yup
      .string()
      .typeError(
        t("typeValidation", {
          field: t("tenant", { count: 1 }),
          type: t("string"),
        }),
      )
      .required(t("requiredValidation", { field: t("tenant", { count: 1 }) })),
    type: yup
      .string()
      .typeError(t("typeValidation", { field: t("type"), type: t("string") }))
      .required(t("requiredValidation", { field: t("type") })),
    description: yup
      .string()
      .nullable()
      .typeError("Description should be a text."),
  })

  const defaultValue: Partial<TenantRequest> = {
    propertyId: "",
    unitId: "",
    tenantId: "",
    type: "",
    priority: "",
    description: "",
  }

  const [getRequest, request] = useLazyGetRequestQuery()
  const [createRequest, createRequestResponse] = useCreateRequestMutation()
  const [updateRequest, updateResponse] = useUpdateRequestMutation()
  const [restoreRequest, restoredRequest] = useRestoreRequestMutation()
  const [deleteRequest, deleteResponse] = useDeleteRequestMutation()
  const [getActivities, activities] = useLazyGetActivitiesQuery()
  const [archiveRequest, archiveResponse] = useArchiveRequestMutation()
  const [toggleStatus, toggleStatusResponse] = useToggleRequestStatusMutation()
  const [getArchivedRequest, archivedRequest] = useLazyGetArchivedRequestQuery()

  const [getProperties, properties] = useLazyGetPropertiesOfLeaseQuery()
  const [getUnits, units] = useLazyGetUnitsOfLeaseQuery()
  const [getTenants, tenants] = useLazyGetTenantsOfLeaseQuery()

  const [getOwners, owners] = useLazyGetOwnersQuery()
  const [assignRequest, assignRequestResponse] = useAssignRequestMutation()
  const [removeRequestFile, removeRequestFileResponse] =
    useRemoveRequestFileMutation()

  const [changeRequestStatus, changeRequestStatusResponse] =
    useChangeRequestStatusMutation()

  useEffect(() => {
    let query =
      userInfo?.role?.key === "owner"
        ? {
            top: 100,
            skip: 0,
            filter: [
              [
                {
                  field: "parentOwnerId",
                  operator: "=",
                  value: ownerInfo?.parentOwnerId,
                },
              ],
              [{ field: "isMaintenance", operator: "=", value: true }],
            ],
          }
        : { top: 100, skip: 0 }
    getOwners(query)
  }, [])

  function _parseOwners() {
    return (
      owners?.data?.data?.map((owner: Owner) => ({
        label: owner?.name as string,
        value: owner?.id as string,
      })) ?? []
    )
  }

  const {
    register,
    setValue,
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<TenantRequest>({
    resolver: yupResolver(schema),
    mode: "all",
  })

  // let selectedCountry = watch("address.country")
  const propertyWatch = watch("propertyId")

  const navigate = useNavigate()

  useEffect(() => {
    if (editMode === "detail") {
      if (params?.type && params?.type === "archived") {
        getArchivedRequest(`${params?.id}`).then((response: any) => {
          reset(response.data)
        })
      } else {
        getRequest(`${params?.id}`).then((response: any) => {
          reset(response.data)
          setValue("propertyId", response?.data?.unit?.propertyId)
          setValue("unitId", response?.data?.unitId)
          setRequestStatus(response?.data?.status)
        })
      }
    } else {
      reset(defaultValue)
    }
  }, [params, editMode, getRequest])

  useEffect(() => {
    if (userInfo?.role?.key == "tenant") {
      setValue("tenantId", tenantInfo?.id)
    }
  }, [tenantInfo])

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getProperties(propertiesCollection)
    } else if (userInfo?.role?.key == "tenant") {
      getProperties({
        ...propertiesCollection,
        filter: [
          [
            {
              field: "ownerId",
              value: tenantInfo?.ownerId,
              operator: "=",
            },
          ],
        ],
      })
    } else {
      getProperties({
        ...propertiesCollection,
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [propertiesCollection])
  useEffect(() => {
    if (propertyWatch) {
      getUnits({
        ...unitCollection,
        filter: [
          [
            {
              field: "propertyId",
              operator: "=",
              value: propertyWatch,
            },
          ],
        ],
      })
    }
  }, [unitCollection, propertyWatch])

  useEffect(() => {
    if (tenantSearch) {
      if (userInfo?.role?.key == "super_admin") {
        getTenants({
          ...tenantsCollection,
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
          ],
        })
      } else {
        getTenants({
          ...tenantsCollection,
          // includes: ["property"],
          filter: [
            [
              { field: "name", operator: "like", value: tenantSearch },
              { field: "phoneNumber", operator: "like", value: tenantSearch },
            ],
            [
              {
                field: "ownerId",
                operator: "=",
                value: ownerInfo?.parentOwnerId,
              },
            ],
          ],
        })
      }
    }
  }, [tenantSearch])


  useEffect(() => {
    if(userInfo?.role?.key == 'tenant'){
      setValue("propertyId", tenantInfo?.propertyId)
      setValue("tenantId", tenantInfo?.id)
      setValue("unitId", tenantInfo?.unitId)
    }
  }, [userInfo])
  useEffect(() => {
    if (propertySearch) {
      if (userInfo?.role?.key == "super_admin") {
        getProperties({
          ...propertiesCollection,
          filter: [
            [{ field: "name", operator: "like", value: propertySearch }],
          ],
        })
      } else if (userInfo?.role?.key == "tenant") {
        getProperties({
          ...propertiesCollection,
          filter: [
            [{ field: "name", operator: "like", value: propertySearch }],
            [
              {
                field: "ownerId",
                operator: "=",
                value: tenantInfo?.ownerId,
              },
            ],
          ],
        })
      } else {
        getProperties({
          ...propertiesCollection,
          filter: [
            [{ field: "name", operator: "like", value: propertySearch }],
            [
              {
                field: "ownerId",
                operator: "=",
                value: ownerInfo?.parentOwnerId,
              },
            ],
          ],
        })
      }
    }
  }, [propertySearch])
  useEffect(() => {
    if (propertyWatch && unitSearch) {
      getUnits({
        ...unitCollection,
        filter: [
          [{ field: "name", operator: "like", value: unitSearch }],
          [{ field: "propertyId", operator: "=", value: propertyWatch }],
        ],
      })
    }
  }, [unitSearch])

  useEffect(() => {
    if (userInfo?.role?.key == "super_admin") {
      getTenants(tenantsCollection)
    } else {
      getTenants({
        ...tenantsCollection,
        // includes: ["property"],
        filter: [
          [
            {
              field: "ownerId",
              value: ownerInfo?.parentOwnerId,
              operator: "=",
            },
          ],
        ],
      })
    }
  }, [tenantsCollection])

  function handleFileRemoval(item: string) {
    removeRequestFile({
      requestId: request?.data?.id as string,
      filename: item,
    })
  }
  function onSubmit(data: any) {
    try {
      const formData = new FormData()

      formData.append(
        "tenantId",
        userInfo?.role?.value == "tenant"
          ? (userInfo?.id as string)
          : data.tenantId,
      )
      formData.append("createdBy", currentUserId as string)
      formData.append(
        "propertyId",
        userInfo?.role?.value == "tenant"
          ? tenantInfo?.propertyId
          : data.propertyId,
      )
      formData.append("unitId", data.unitId)
      formData.append("description", data.description)
      formData.append("type", data.type)
      formData.append("priority", data.priority)
      if (data.files && data.files.length > 0) {
        data.files.forEach((file: any, i: number) => {
          formData.append("files", file)
        })
      }

      if (editMode === "new") {
        createRequest(formData).then((response: any) => {
          if (!response?.error) {
            reset(defaultValue)
          }
        })
      } else {
        formData.append("id", params.id as string)
        updateRequest(formData)
      }
    } catch (e: any) {
      console.log("🚀 ~ onSubmit ~ e:", e)
    }
  }

  const onError = (error: any) => {
    console.log("🚀 ~ onError ~ error:", error)
  }

  function _parseProperties() {
    let data: { label: string; value: string }[] = []

    properties?.data?.data?.map((property: Property) => {
      data.push({
        label: property?.name as string,
        value: property?.id as string,
      })
    })
    return data
  }
  function _parseUnits() {
    let data: { label: string; value: string }[] = []

    units?.data?.data?.map((unit: Unit) => {
      data.push({ label: unit?.name as string, value: unit?.id as string })
    })

    return data
  }
  function _parseTenants() {
    let data: { label: string; value: string }[] = []

    tenants?.data?.data?.map((tenant: Tenant) => {
      data.push({
        label: `${tenant?.name as string} (${tenant?.phoneNumber as string})`,
        value: tenant?.id as string,
      })
    })

    return data
  }

  return (
    // <div className="bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl p-6 border border-gray-700 mb-8">
    <ActivityLogWrapperComponent
      title={
        editMode === "new" ? (
          t("newRequest")
        ) : (
          <div className="h-full space-x-2 flex items-center">
            <div>{request?.data?.name}</div>
          </div>
        )
      }
      item={request}
      path={`/owner/detail/${params?.type}/${params.id}`}
      id={params.id ?? ""}
    >
      <LoadingOverlay
        visible={
          (editMode === "new"
            ? createRequestResponse.isLoading
            : updateResponse.isLoading) || request.isLoading
        }
      />
      <>
        <Tabs defaultValue="main">
          <Tabs.List>
            <Tabs.Tab value="main" icon={<IconUser size={14} />}>
              {t("basicRequestInfo")}
            </Tabs.Tab>
            {/* {editMode === "detail" && (
              <>
                <Tabs.Tab value="role">{t("roleAndPermission")}</Tabs.Tab>
              </>
            )} */}
          </Tabs.List>

          <Tabs.Panel value="main" pt="xs">
            <div className="flex  justify-center h-full">
              <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="w-full gap-y-4 dark:text-white"
              >
                <Accordion
                  defaultValue={["info"]}
                  multiple
                  variant="separated"
                  classNames={{
                    control:
                      "bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-lg shadow-lg rounded-xl border border-gray-700",
                  }}
                >
                  <Accordion.Item value="info">
                    <Accordion.Control>
                      {t("basicRequestInfo")}
                    </Accordion.Control>
                    <Accordion.Panel className="bg-gray-800">
                      <div className="flex-col space-y-4 bg-gray-800">
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          {userInfo?.role?.key != "tenant" && (
                            <>
                              <LoadingInputWrapper
                                isLoading={tenants.isFetching}
                                // topPadding="mt-11"
                              >
                                <Controller
                                  control={control}
                                  name="tenantId"
                                  render={({ field }) => (
                                    <Select
                                      classNames={{ label: "dark:text-white" }}
                                      className="w-full"
                                      onKeyDown={debounce(
                                        (event: any) =>
                                          setTenantSearch(event?.target?.value),
                                        1000,
                                      )}
                                      searchable
                                      clearable
                                      withAsterisk
                                      error={
                                        errors.tenantId &&
                                        `${errors?.tenantId?.message}`
                                      }
                                      label={t("tenant", { count: 1 })}
                                      placeholder={t("tenant", { count: 1 })}
                                      data={_parseTenants()}
                                      {...field}
                                    />
                                  )}
                                />
                              </LoadingInputWrapper>
                              <LoadingInputWrapper
                                isLoading={properties.isFetching}
                                // topPadding="mt-11"
                              >
                                <Controller
                                  control={control}
                                  name="propertyId"
                                  render={({ field }) => (
                                    <Select
                                      classNames={{ label: "dark:text-white" }}
                                      className="w-full"
                                      onKeyDown={debounce(
                                        (event: any) =>
                                          setPropertySearch(
                                            event?.target?.value,
                                          ),
                                        1000,
                                      )}
                                      searchable
                                      clearable
                                      withAsterisk
                                      error={
                                        errors.propertyId &&
                                        `${errors?.propertyId?.message}`
                                      }
                                      label={t("property", { count: 1 })}
                                      placeholder={t("property", { count: 1 })}
                                      data={_parseProperties()}
                                      {...field}
                                    />
                                  )}
                                />
                              </LoadingInputWrapper>
                            </>
                          )}
                        </div>

                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <LoadingInputWrapper
                            isLoading={units.isFetching}
                            topPadding="mt-11"
                          >
                            <Controller
                              control={control}
                              name="unitId"
                              render={({ field }) => (
                                <Select
                                  classNames={{ label: "dark:text-white" }}
                                  className="w-full"
                                  withAsterisk
                                  onKeyDown={debounce(
                                    (event: any) =>
                                      setUnitSearch(event?.target?.value),
                                    1000,
                                  )}
                                  searchable
                                  clearable
                                  error={
                                    errors.unitId &&
                                    `${errors?.unitId?.message}`
                                  }
                                  description={t("unitDesc")}
                                  label={t("unit", { count: 1 })}
                                  placeholder={t("unit", { count: 1 })}
                                  data={_parseUnits()}
                                  {...field}
                                />
                              )}
                            />
                          </LoadingInputWrapper>

                          <Controller
                            control={control}
                            name="type"
                            render={({ field }) => (
                              <Select
                                classNames={{ label: "dark:text-white" }}
                                className="w-full mt-[1.25rem]"
                                withAsterisk
                                searchable
                                clearable
                                error={
                                  errors.type && `${errors?.type?.message}`
                                }
                                // description={t("unitDesc")}
                                label={t("type", { count: 1 })}
                                placeholder={t("type", { count: 1 })}
                                data={[
                                  {
                                    label: "Maintenance",
                                    value: "Maintenance",
                                  },
                                  { label: "Rent Amount", value: "RentAmount" },
                                ]}
                                {...field}
                              />
                            )}
                          />
                        </div>
                        <div className="flex flex-col md:flex-row w-full space-x-0  md:space-x-4  justify-between">
                          <Controller
                            control={control}
                            name="priority"
                            render={({ field }) => (
                              <Select
                                classNames={{ label: "dark:text-white" }}
                                className="w-full"
                                withAsterisk
                                searchable
                                clearable
                                error={
                                  errors.priority &&
                                  `${errors?.priority?.message}`
                                }
                                label={t("priority")}
                                placeholder={t("priority")}
                                data={[t("high"), t("medium"), t("low")]}
                                {...field}
                              />
                            )}
                          />

                          <div className="w-full">
                            <Controller
                              control={control}
                              name="files"
                              render={({ field }) => (
                                <FileInput
                                  withAsterisk
                                  clearable
                                  multiple
                                  className="w-full"
                                  error={
                                    errors.files && `${errors.files?.message}`
                                  }
                                  label={t("file")}
                                  //@ts-ignore
                                  placeholder={t("file")}
                                  icon={<IconUpload size={14} />}
                                  {...field}
                                />
                              )}
                            />
                            {request?.data?.minioFiles &&
                              request?.data?.minioFiles?.length > 0 && (
                                <span
                                  className="cursor-pointer text-primary font-bold my-2 hover:underline"
                                  onClick={() => {
                                    setSelectedFiles(
                                      request?.data?.minioFiles as string[],
                                    )
                                    fileOpen()
                                  }}
                                >
                                  {/* {t("viewLeaseFile")} */}
                                  <p>
                                    View (
                                    {request?.data?.minioFiles?.length ?? 0})
                                    Files
                                  </p>
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row w-[49%] space-x-0  md:space-x-4  justify-between">
                          <Textarea
                            classNames={{ label: "dark:text-white" }}
                            className="w-full"
                            error={
                              errors.description &&
                              `${errors?.description?.message}`
                            }
                            withAsterisk
                            label={t("description")}
                            placeholder={t("description")}
                            {...register("description")}
                          />
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>

                <div className="w-full flex justify-end items-center">
                  <Group position="right" mt="xl">
                    {editMode === "new" && (
                      <>
                        <Button
                          classNames={{ label: "flex space-x-1 " }}
                          variant="default"
                          className="dark:text-white hover:bg-primary hover:dark:bg_dark_primary hover:text-white"
                          type="reset"
                          size="xs"
                        >
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="fill-current h-6"
                              viewBox="0 0 32 32"
                            >
                              <path d="M18.90625 4.09375C18.101563 4.09375 17.265625 4.367188 16.625 4.9375L16.625 4.96875L16.59375 5L4.90625 16.59375C3.695313 17.804688 3.703125 19.777344 4.84375 21.0625L4.875 21.09375L4.90625 21.09375L10.90625 27.09375C11.511719 27.699219 12.320313 27.992188 13.125 28L28 28L28 26L16.5 26L27 15.5C28.265625 14.234375 28.304688 12.210938 27.09375 11L21.09375 5C20.488281 4.394531 19.710938 4.09375 18.90625 4.09375 Z M 18.875 6.125C19.195313 6.125 19.492188 6.210938 19.6875 6.40625L25.6875 12.40625C26.074219 12.792969 26.128906 13.558594 25.59375 14.09375L20.5625 19.125L12.90625 11.46875L17.96875 6.4375L18 6.40625C18.253906 6.195313 18.570313 6.125 18.875 6.125 Z M 11.46875 12.90625L19.125 20.5625L14.03125 25.65625C14.019531 25.664063 14.011719 25.679688 14 25.6875C13.484375 26.117188 12.691406 26.066406 12.3125 25.6875L6.34375 19.75C6.328125 19.730469 6.328125 19.707031 6.3125 19.6875C5.902344 19.171875 5.9375 18.375 6.3125 18Z" />
                            </svg>
                          </span>
                          <span>{t("reset")}</span>
                        </Button>
                        <Button
                          variant="filled"
                          className="bg-primary text-white"
                          type="submit"
                          classNames={{ label: "flex space-x-1" }}
                          size={"xs"}
                          loading={createRequestResponse.isLoading}
                        >
                          <span>
                            <IconDeviceFloppy size={22} strokeWidth={1.5} />
                          </span>
                          <span>{t("submit")}</span>
                        </Button>
                      </>
                    )}
                    {editMode === "detail" && (
                      <>
                        {(params?.type === "archived" &&
                          archivedRequest?.data?.deletedAt) ||
                        (params?.type === "active" &&
                          request?.data?.deletedAt) ? (
                          <>
                            <Confirmation
                              type={"danger"}
                              message={t("permanentDeleteMessage")}
                              onYes={() => deleteRequest(`${params?.id}`)}
                              header={t("permanentDeleteTitle")}
                            >
                              <Button
                                variant="filled"
                                loading={deleteResponse.isLoading}
                                className={`bg-danger text-white flex space-x-2 items-center`}
                                type="button"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                              >
                                <IconTrash size={20} strokeWidth={1.5} />
                                <span>{t("delete")}</span>{" "}
                              </Button>
                            </Confirmation>
                            <Confirmation
                              type={"notify"}
                              message={t("restoreMessage")}
                              onYes={() => restoreRequest(`${params?.id}`)}
                              header={t("restoreTitle")}
                            >
                              <Button
                                variant="filled"
                                loading={restoredRequest.isLoading}
                                className={`bg-green-500 text-white flex space-x-2 items-center`}
                                type="button"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                              >
                                <IconArrowBackUp size={20} strokeWidth={1.5} />
                                <span>{t("restore")}</span>
                              </Button>
                            </Confirmation>
                          </>
                        ) : (
                          <>
                            {" "}
                            {userInfo?.role?.key == "owner" &&
                              !request?.data?.maintenanceId && (
                                <Button
                                  variant="filled"
                                  className="bg-primary text-white"
                                  type="button"
                                  classNames={{ label: "flex space-x-1" }}
                                  size={"xs"}
                                  onClick={() => open()}
                                  // loading={createRequestResponse.isLoading}
                                >
                                  {/* <span>
                                <IconDeviceFloppy size={22} strokeWidth={1.5} />
                              </span> */}
                                  <span>{t("assignMaintenanceWorker")}</span>
                                </Button>
                              )}
                            <Confirmation
                              type={"notify"}
                              message={t("archiveMessage", {
                                model: t("request"),
                              })}
                              header={t("archiveTitle", {
                                name: request?.data?.id,
                              })}
                              result={"single"}
                              resultRequired={true}
                              selectorLabel={t("selectAReason", {
                                model: t("request"),
                              })}
                              labelDescription={t("archiveWhy", {
                                model: t("request"),
                              })}
                              options={Constants(t)?.ArchiveReason?.map(
                                (reason) => {
                                  return { label: reason, value: reason }
                                },
                              )}
                              customInput={true}
                              yesText={t("archive")}
                              onYes={(reason: string) => {
                                archiveRequest({
                                  id: `${params.id}`,
                                  reason: reason,
                                })
                              }}
                            >
                              <Button
                                variant="filled"
                                loading={archiveResponse.isLoading}
                                className="bg-danger text-white"
                                type="button"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                              >
                                <span>
                                  <IconArchive size={22} strokeWidth={1.5} />
                                </span>
                                <span>{t("archive")}</span>
                              </Button>
                            </Confirmation>
                            <Confirmation
                              type={"notify"}
                              message={
                                <span>
                                  {t("activateMessage", {
                                    action: request?.data?.enabled
                                      ? t("deactivation")
                                      : t("activation"),
                                  })}
                                </span>
                              }
                              onYes={() => toggleStatus(`${params.id}`)}
                              header={t("activateTitle", {
                                action: request.data?.enabled
                                  ? t("deactivation")
                                  : t("activation"),
                              })}
                            >
                              <Button
                                variant="filled"
                                loading={toggleStatusResponse.isLoading}
                                className={`${
                                  request.data?.enabled
                                    ? " bg-danger"
                                    : "bg-green-500"
                                } text-white`}
                                type="button"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                              >
                                <span>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="fill-current h-6"
                                    viewBox="0 0 48 48"
                                  >
                                    <path d="M23.5 5C17.16639 5 12 10.16639 12 16.5C12 18.130861 12.341389 19.689962 12.957031 21.099609 A 1.50015 1.50015 0 1 0 15.707031 19.900391C15.252673 18.860038 15 17.713139 15 16.5C15 11.78761 18.78761 8 23.5 8C28.21239 8 32 11.78761 32 16.5C32 17.08427 31.94138 17.652314 31.830078 18.201172 A 1.50015 1.50015 0 1 0 34.769531 18.798828C34.92023 18.055686 35 17.28573 35 16.5C35 10.16639 29.83361 5 23.5 5 z M 23.5 12C21.032499 12 19 14.032499 19 16.5L19 25.759766L18.138672 25.404297C14.483804 23.900444 10.334734 26.436466 10.005859 30.375 A 1.50015 1.50015 0 0 0 10.693359 31.765625L19.957031 37.667969C20.601036 38.078718 21.151526 38.620029 21.576172 39.255859L23.033203 41.441406 A 1.50015 1.50015 0 0 0 23.035156 41.441406C23.803886 42.591886 25.189849 43.186167 26.554688 42.945312L31.882812 42.005859C33.603893 41.703285 34.998876 40.422039 35.449219 38.734375 A 1.50015 1.50015 0 0 0 35.451172 38.726562L37.832031 29.576172C38.653863 26.49462 36.64066 23.318721 33.501953 22.748047L28 21.748047L28 16.5C28 14.032499 25.967501 12 23.5 12 z M 23.5 15C24.346499 15 25 15.653501 25 16.5L25 23 A 1.50015 1.50015 0 0 0 26.232422 24.476562L32.964844 25.699219C34.424137 25.964545 35.315668 27.370273 34.933594 28.802734 A 1.50015 1.50015 0 0 0 34.929688 28.8125L32.550781 37.960938C32.399124 38.529274 31.940201 38.949356 31.363281 39.050781 A 1.50015 1.50015 0 0 0 31.363281 39.052734L26.033203 39.992188C25.834042 40.027338 25.642567 39.944908 25.529297 39.775391L24.072266 37.591797C23.417016 36.610136 22.565912 35.77367 21.570312 35.138672 A 1.50015 1.50015 0 0 0 21.568359 35.138672L13.251953 29.835938C13.814585 28.352335 15.413607 27.528548 16.996094 28.179688L19.929688 29.386719 A 1.50015 1.50015 0 0 0 22 28L22 16.5C22 15.653501 22.653501 15 23.5 15 z" />
                                  </svg>
                                </span>
                                {request?.data?.enabled ? (
                                  <span>{t("deactivate")}</span>
                                ) : (
                                  <span>{t("activate")}</span>
                                )}
                              </Button>
                            </Confirmation>
                            {(userInfo?.role?.key == "owner" ||
                              userInfo?.role?.key == "super_admin") && (
                              <Button
                                variant="filled"
                                className="bg-primary text-white"
                                type="button"
                                classNames={{ label: "flex space-x-1" }}
                                size={"xs"}
                                onClick={openStatus}
                                // loading={updateResponse.isLoading}
                              >
                                <span>
                                  <IconStatusChange
                                    size={22}
                                    strokeWidth={1.5}
                                  />
                                </span>
                                <span>{t("changeStatus")}</span>
                              </Button>
                            )}
                            <Button
                              variant="filled"
                              className="bg-primary text-white"
                              type="submit"
                              classNames={{ label: "flex space-x-1" }}
                              size={"xs"}
                              loading={updateResponse.isLoading}
                            >
                              <span>
                                <IconDeviceFloppy size={22} strokeWidth={1.5} />
                              </span>
                              <span>{t("update")}</span>
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </Group>
                </div>
              </form>

              <Modal
                opened={opened}
                onClose={() => {
                  close()
                }}
                // className="my-modal-content"
                withCloseButton={false}
                title={t("assignMaintenanceWorker")}
                padding={20}
                size={"md"}
              >
                <div className="w-full">
                  <div className="flex w-full space-x-4  justify-center">
                    <Select
                      radius={"xs"}
                      classNames={{ label: "dark:text-white" }}
                      className="w-full"
                      // error={errors?.method && `${errors?.method?.message}`}
                      label={t("maintenanceWorker")}
                      dropdownPosition="bottom"
                      placeholder={t("maintenanceWorker")}
                      data={_parseOwners()}
                      value={selectedOwner}
                      onChange={(owner: string) => setSelectedOwner(owner)}
                      withAsterisk
                    />
                  </div>
                </div>
                <Group position="right" mt="xl">
                  <>
                    <Button
                      variant="filled"
                      className="bg-primary text-white"
                      type="button"
                      classNames={{ label: "flex space-x-1" }}
                      size={"xs"}
                      loading={assignRequestResponse.isLoading}
                      onClick={() =>
                        assignRequest({
                          id: params?.id as string,
                          maintenanceId: selectedOwner as string,
                        }).then(() => {
                          close()
                          notification(
                            "success",
                            t("maintenanceAssignedSuccessfully"),
                          )
                        })
                      }
                    >
                      <span>
                        <IconDeviceFloppy size={22} strokeWidth={1.5} />
                      </span>
                      <span>{t("submit")}</span>
                    </Button>
                  </>
                </Group>
              </Modal>

              <Modal
                opened={statusOpened}
                onClose={() => {
                  closeStatus()
                }}
                // className="my-modal-content"
                withCloseButton={false}
                title={t("changeRequestStatus")}
                padding={20}
                size={"md"}
              >
                {/* <form onSubmit={handleSubmit(onSubmit, onError)} className={"mt-2"}> */}
                <div className="w-full">
                  <div className="flex w-full space-x-4  justify-center">
                    <Select
                      radius={"xs"}
                      classNames={{ label: "dark:text-white" }}
                      className="w-full"
                      // error={errors?.method && `${errors?.method?.message}`}
                      label={t("status")}
                      dropdownPosition="bottom"
                      placeholder={t("status")}
                      data={[
                        {
                          label: TenantRequestStatus.Pending,
                          value: TenantRequestStatus.Pending,
                        },
                        {
                          label: TenantRequestStatus.InProgress,
                          value: TenantRequestStatus.InProgress,
                        },
                        {
                          label: TenantRequestStatus.Completed,
                          value: TenantRequestStatus.Completed,
                        },
                      ]}
                      value={requestStatus}
                      onChange={(val: TenantRequestStatus) =>
                        setRequestStatus(val)
                      }
                      withAsterisk
                    />
                  </div>
                </div>
                <Group position="right" mt="xl">
                  <>
                    <Button
                      variant="filled"
                      className="bg-primary text-white"
                      type="button"
                      classNames={{ label: "flex space-x-1" }}
                      size={"xs"}
                      loading={changeRequestStatusResponse.isLoading}
                      onClick={() =>
                        changeRequestStatus({
                          id: params?.id as string,
                          status: requestStatus,
                        }).then(() => closeStatus())
                      }
                    >
                      <span>
                        <IconDeviceFloppy size={22} strokeWidth={1.5} />
                      </span>
                      <span>{t("submit")}</span>
                    </Button>
                  </>
                </Group>
                {/* </form> */}
              </Modal>
            </div>
          </Tabs.Panel>
        </Tabs>
      </>
      <FileViewerModal
        open={fileOpen}
        close={fileClose}
        fileType={t("request", { count: 1 })}
        files={selectedFiles as string[]}
        opened={fileOpened}
        remove={handleFileRemoval}
        removeLoading={removeRequestFileResponse?.isLoading}
        fileNames={request?.data?.fileNames as string[]}
      />
    </ActivityLogWrapperComponent>
  )
}
